import { useEffect, useMemo, useState } from 'react';
import { Layers, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import type { SubscriberRow } from './types';
import {
  channelLabel,
  channelsMapFromKeys,
  channelsToKeyArray,
  visibilityLabel,
  type ChannelRow,
} from '../../lib/channels';
import { subscriberSourceLabel } from '../../lib/subscriberSource';

function subscriberMatcher(row: SubscriberRow): { column: string; value: string | number } | null {
  const id = row.id;
  if (typeof id === 'string' && id) return { column: 'id', value: id };
  if (typeof id === 'number' && Number.isFinite(id)) return { column: 'id', value: id };
  const email = typeof row.email === 'string' ? row.email.trim() : '';
  if (email) return { column: 'email', value: email };
  return null;
}

function displayEmail(row: SubscriberRow): string {
  const email = typeof row.email === 'string' ? row.email.trim() : '';
  return email || '(no email)';
}

interface SubscriberBulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRows: SubscriberRow[];
  channelCatalog: ChannelRow[];
  knownSources: string[];
  onSuccess: () => void;
}

export default function SubscriberBulkEditModal({
  isOpen,
  onClose,
  selectedRows,
  channelCatalog,
  knownSources,
  onSuccess,
}: SubscriberBulkEditModalProps) {
  const [applySource, setApplySource] = useState(false);
  const [sourceValue, setSourceValue] = useState('');
  const [applyChannels, setApplyChannels] = useState(false);
  const [channelsToAdd, setChannelsToAdd] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const catalogSlugs = useMemo(() => new Set(channelCatalog.map((c) => c.slug)), [channelCatalog]);

  useEffect(() => {
    if (!isOpen) return;
    setApplySource(false);
    setSourceValue('');
    setApplyChannels(false);
    setChannelsToAdd([]);
  }, [isOpen, selectedRows]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, saving]);

  const channelsByVisibility = useMemo(() => {
    const groups: Record<string, ChannelRow[]> = { public: [], private: [], internal: [] };
    for (const c of channelCatalog) {
      groups[c.visibility]?.push(c);
    }
    for (const slug of channelsToAdd) {
      if (!catalogSlugs.has(slug)) {
        groups.private.push({
          id: slug,
          slug,
          label: channelLabel(slug, channelCatalog),
          description: null,
          visibility: 'private',
          sort_order: 999,
          active: true,
          created_at: '',
          updated_at: '',
        });
      }
    }
    return groups;
  }, [channelCatalog, channelsToAdd, catalogSlugs]);

  const toggleChannel = (slug: string) => {
    setChannelsToAdd((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  const canApply =
    selectedRows.length > 0 &&
    ((applySource && sourceValue.trim().length > 0) || (applyChannels && channelsToAdd.length > 0));

  const handleApply = async () => {
    if (!canApply) {
      toast.error('Choose at least one change to apply');
      return;
    }

    setSaving(true);
    let updated = 0;
    let skipped = 0;
    const errors: string[] = [];

    for (const row of selectedRows) {
      const matcher = subscriberMatcher(row);
      if (!matcher) {
        skipped += 1;
        errors.push(`${displayEmail(row)}: missing id/email`);
        continue;
      }

      const patch: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (applySource) {
        patch.source = sourceValue.trim();
      }

      if (applyChannels) {
        const merged = channelsMapFromKeys([...channelsToKeyArray(row.channels), ...channelsToAdd]);
        patch.channels = merged;
      }

      try {
        const { error } = await supabase.from('subscribers').update(patch).eq(matcher.column, matcher.value);
        if (error) throw error;
        updated += 1;
      } catch (e: unknown) {
        skipped += 1;
        const msg = e instanceof Error ? e.message : 'Update failed';
        errors.push(`${displayEmail(row)}: ${msg}`);
      }
    }

    setSaving(false);

    if (updated > 0) {
      toast.success(`Updated ${updated} connection${updated === 1 ? '' : 's'}`);
      onSuccess();
      onClose();
    }

    if (skipped > 0) {
      toast.error(
        `${skipped} could not be updated${errors.length ? ` — ${errors.slice(0, 2).join('; ')}${errors.length > 2 ? '…' : ''}` : ''}`,
      );
    } else if (updated === 0) {
      toast.error('No connections were updated');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      role="presentation"
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div
        className="w-full max-w-lg bg-white shadow-xl dark:bg-gray-800 flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-edit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-emerald-600" aria-hidden />
            <h2 id="bulk-edit-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Bulk edit
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto px-5 py-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Apply changes to{' '}
            <span className="font-medium text-gray-900 dark:text-white">{selectedRows.length}</span>{' '}
            selected connection{selectedRows.length === 1 ? '' : 's'}. Only checked fields are updated.
          </p>

          <div className="space-y-2">
            <label className="inline-flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={applySource}
                onChange={(e) => setApplySource(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm font-medium text-gray-900 dark:text-white">Set source</span>
            </label>
            {applySource ? (
              <div className="pl-6">
                <input
                  type="text"
                  list="bulk-edit-known-sources"
                  value={sourceValue}
                  onChange={(e) => setSourceValue(e.target.value)}
                  placeholder="e.g. calendly, luma, manual"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <datalist id="bulk-edit-known-sources">
                  {knownSources.map((source) => (
                    <option key={source} value={source}>
                      {subscriberSourceLabel(source) || source}
                    </option>
                  ))}
                </datalist>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Replaces the source on every selected connection.
                </p>
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <label className="inline-flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={applyChannels}
                onChange={(e) => setApplyChannels(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm font-medium text-gray-900 dark:text-white">Add to channels</span>
            </label>
            {applyChannels ? (
              <div className="pl-6 space-y-3">
                {(['public', 'private', 'internal'] as const).map((vis) => {
                  const items = channelsByVisibility[vis];
                  if (!items?.length) return null;
                  return (
                    <div key={vis}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                        {visibilityLabel(vis)}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {items.map((ch) => {
                          const active = channelsToAdd.includes(ch.slug);
                          return (
                            <button
                              key={ch.slug}
                              type="button"
                              onClick={() => toggleChannel(ch.slug)}
                              aria-pressed={active}
                              className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                                active
                                  ? 'bg-emerald-500 text-white border-emerald-500'
                                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                              }`}
                              title={ch.description ?? ch.slug}
                            >
                              {ch.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {channelCatalog.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">No channels configured yet.</p>
                ) : null}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Adds selected channels without removing existing ones.
                </p>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleApply()}
            disabled={!canApply || saving}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Applying…' : `Apply to ${selectedRows.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
